import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const consentReminderDialogInputs = [
  {
    name: "title",
    type: "string",
    helperText: "Sets the consents reminder dialog title",
    required: true,
  },
  {
    name: "subtitle",
    type: "string",
    helperText: "Sets the consents reminder dialog subtitle",
  },
  {
    name: "description",
    type: "string",
    helperText: "Sets the consents reminder dialog description",
  },
  {
    name: "isMarketingConsentsConfirmationEnabled",
    type: "boolean",
    defaultValue: false,
    helperText: "Displays the marketing consents confirmation experience",
  },
  {
    name: "desktopImage",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    helperText: "Sets the consents reminder dialog desktop image",
    required: true,
  },
  {
    name: "mobileImage",
    type: "file",
    allowedFileTypes: IMAGE_FILE_TYPES,
    helperText: "Sets the consents reminder dialog mobile image",
  },
  {
    name: "altImageText",
    type: "string",
    helperText: "Text for the consent reminder image",
    required: true,
  },
  {
    name: "offerConsentName",
    type: "string",
    showIf: 'options.get("isMarketingConsentsConfirmationEnabled") === true',
    helperText: "Sets the offer consent name",
  },
  {
    name: "productInformationConsentName",
    type: "string",
    showIf: 'options.get("isMarketingConsentsConfirmationEnabled") === true',
    helperText: "Sets the offer product information consent name",
  },
  {
    name: "ploomClubConsentName",
    type: "string",
    showIf: 'options.get("isMarketingConsentsConfirmationEnabled") === true',
    helperText: "Sets the Ploom Club consent name",
  },
  {
    name: "consentsLegalText",
    type: "richText",
    showIf: 'options.get("isMarketingConsentsConfirmationEnabled") === true',
    helperText: "Sets the legal text below the consents",
  },
  {
    name: "consentOptionsListType",
    type: "string",
    enum: ["default", "extended"],
    defaultValue: "default",
    showIf: 'options.get("isMarketingConsentsConfirmationEnabled") === true',
    helperText: "Sets the options list shown for each consent",
  },
  {
    name: "isClosable",
    type: "boolean",
    defaultValue: false,
    helperText: "Sets the boolean to allow/disallow close popup until consents are chosen",
  },
  {
    name: "confirmChoiceText",
    type: "string",
    defaultValue: "Confirm my choices",
    helperText: "Sets the text for the primary confirm button",
    required: true,
  },
  {
    name: "primaryButtonHref",
    type: "url",
    helperText: "Sets the primary button redirect URL",
  },
  {
    name: "secondaryButtonText",
    type: "string",
    helperText: "Sets the text for the secondary button",
  },
  {
    name: "secondaryButtonHref",
    type: "url",
    helperText: "Sets the secondary button redirect URL",
  },
  {
    name: "maxOccurrencesPerYear",
    type: "number",
    helperText: "Sets the number of occurrences for consent reminder popup per year",
  },
  {
    name: "consentExpirationLimit",
    friendlyName: "Days before expiry",
    type: "number",
    helperText: "Sets consent expiration limit in days",
  },
  {
    name: "popUpDelay",
    friendlyName: "Pop-up delay (seconds)",
    type: "number",
    helperText: "Sets the delay for the consent reminder popup",
    defaultValue: 0,
  },
] as const satisfies Inputs;

export const ConsentReminderDialogComponent: RegisteredComponent = {
  name: "Consent Reminder Dialog",
  component: dynamic(
    async () =>
      withValidation(
        consentReminderDialogInputs,
        (await import("./ConsentReminderDialog")).ConsentReminderDialog,
        "ConsentReminderDialog"
      ),
    { ssr: false }
  ),
  models: [getBuilderModelName("consent-reminder-dialog")],
  inputs: consentReminderDialogInputs,
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderComponents: true,
    builderContext: true,
  },
  defaultChildren: [],
};
